import { useCallback, useRef } from 'react';

import { LockedComposer } from './LockedComposer';
import { SeatUpgradeCard } from './SeatUpgradeCard';

/**
 * Chat-lookalike paywall screen for viewer-seat users.
 *
 * Renders the chat page frame (header + scrollable message area + composer)
 * without issuing any conversation queries. The composer is the shared
 * LockedComposer facade; activating it scrolls to the upgrade card centered
 * in the message area.
 */
export function SeatGatedChat() {
  const upgradeCardRef = useRef<HTMLDivElement>(null);

  const handleComposerActivate = useCallback(() => {
    upgradeCardRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
    upgradeCardRef.current?.focus({ preventScroll: true });
  }, []);

  return (
    <div className='h-full p-0 m-0 overflow-hidden flex flex-col'>
      <div className='flex flex-1 h-full overflow-hidden p-0 m-0'>
        <div className='flex flex-col bg-bg-primary w-full'>
          {/* Header — matches the non-builder ReasoningChat header shell.
              Just the destination name: there is no conversation here to
              breadcrumb into. */}
          <div className='bg-bg-primary border-b border-border-secondary'>
            <div className='flex items-center px-3 sm:px-6 py-4'>
              <p className='text-base leading-5 font-medium text-black dark:text-white'>
                Chat
              </p>
            </div>
          </div>

          {/* Message area — upgrade card centered; no conversation fetches */}
          <div
            className='flex-1 min-w-0 overflow-y-auto overflow-x-hidden relative'
            tabIndex={-1}
          >
            <div className='flex h-full items-center justify-center p-8'>
              <div
                ref={upgradeCardRef}
                className='w-full max-w-md outline-none'
                tabIndex={-1}
              >
                <SeatUpgradeCard feature='chat' variant='full' />
              </div>
            </div>
          </div>

          {/* Composer facade — activation scrolls/focuses the upgrade card. */}
          <div className='px-4 pb-6 pt-2'>
            <div className='mx-auto w-full max-w-3xl'>
              <LockedComposer onActivate={handleComposerActivate} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
